import { useState, useEffect } from 'react';
import { adminAPI, appointmentAPI } from '../../lib/api';
import { 
  VideoCamera, 
  User,
  Calendar,
  Clock,
  Phone,
  Envelope,
  MagnifyingGlass,
  CheckCircle,
  XCircle,
  HourglassHigh,
  CaretDown,
  CaretUp,
  Stethoscope,
  FirstAidKit
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function AdminTelehealthPage() {
  const [appointments, setAppointments] = useState([]);
  const [providers, setProviders] = useState([]);
  const [cmas, setCmas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    loadData();
  }, [selectedDate]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Get all appointments
      const appointmentsRes = await appointmentAPI.getAll();
      // Filter for telehealth appointments
      const telehealthAppts = (appointmentsRes.data || []).filter(apt => 
        apt.appointment_type?.toLowerCase().includes('telehealth') ||
        apt.appointment_type?.toLowerCase().includes('virtual') ||
        apt.meeting_room_url
      );
      setAppointments(telehealthAppts);

      // Get staff users
      const usersRes = await adminAPI.getUsers();
      setProviders(usersRes.data.filter(u => u.role === 'provider'));
      setCmas(usersRes.data.filter(u => u.role === 'cma'));
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load telehealth data');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'scheduled':
        return <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full flex items-center gap-1"><Clock size={12} /> Scheduled</span>;
      case 'checked_in':
        return <span className="px-2 py-1 bg-teal-100 text-teal-700 text-xs rounded-full flex items-center gap-1"><CheckCircle size={12} /> Checked In</span>;
      case 'in_progress':
        return <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full flex items-center gap-1"><VideoCamera size={12} /> In Progress</span>;
      case 'completed':
        return <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full flex items-center gap-1"><CheckCircle size={12} /> Completed</span>;
      case 'cancelled':
        return <span className="px-2 py-1 bg-slate-100 text-slate-500 text-xs rounded-full flex items-center gap-1"><XCircle size={12} /> Cancelled</span>;
      case 'no_show':
        return <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full flex items-center gap-1"><XCircle size={12} /> No Show</span>;
      default:
        return <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">{status}</span>;
    }
  };

  // Filter appointments
  const filteredAppointments = appointments.filter(apt => {
    const matchesSearch = apt.patient_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          apt.provider_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!matchesSearch) return false;
    
    if (filterStatus === 'all') return true;
    return apt.status === filterStatus;
  });

  // Separate into today's and upcoming
  const today = new Date().toISOString().split('T')[0];
  const todayAppointments = filteredAppointments.filter(apt => 
    apt.scheduled_time?.startsWith(today)
  );
  const upcomingAppointments = filteredAppointments.filter(apt => 
    apt.scheduled_time && new Date(apt.scheduled_time) > new Date() && !apt.scheduled_time.startsWith(today)
  );
  const pastAppointments = filteredAppointments.filter(apt => 
    apt.scheduled_time && new Date(apt.scheduled_time) < new Date() && !apt.scheduled_time.startsWith(today)
  );

  const stats = {
    todayCount: todayAppointments.length,
    upcomingCount: upcomingAppointments.length,
    inProgress: appointments.filter(a => a.status === 'in_progress').length,
    completedToday: todayAppointments.filter(a => a.status === 'completed').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Telehealth Overview</h1>
          <p className="text-slate-500">Monitor and manage all virtual appointments</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card-clinical p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Calendar size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{stats.todayCount}</p>
              <p className="text-sm text-slate-500">Today's Visits</p>
            </div>
          </div>
        </div>
        <div className="card-clinical p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <VideoCamera size={20} className="text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{stats.inProgress}</p>
              <p className="text-sm text-slate-500">In Progress</p>
            </div>
          </div>
        </div>
        <div className="card-clinical p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle size={20} className="text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{stats.completedToday}</p>
              <p className="text-sm text-slate-500">Completed Today</p>
            </div>
          </div>
        </div>
        <div className="card-clinical p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-100 rounded-lg">
              <HourglassHigh size={20} className="text-teal-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{stats.upcomingCount}</p>
              <p className="text-sm text-slate-500">Upcoming</p>
            </div>
          </div>
        </div>
      </div>

      {/* Staff Overview */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="card-clinical p-4">
          <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <Stethoscope size={18} className="text-purple-600" />
            Providers ({providers.length})
          </h3>
          <div className="space-y-2">
            {providers.slice(0, 5).map(provider => (
              <div key={provider.id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                    <Stethoscope size={14} className="text-purple-600" />
                  </div>
                  <span className="text-sm font-medium">{provider.first_name} {provider.last_name}</span>
                </div>
                <span className="text-xs text-slate-500">{provider.email}</span>
              </div>
            ))}
            {providers.length === 0 && (
              <p className="text-sm text-slate-500 text-center py-2">No providers registered</p>
            )}
          </div>
        </div>
        <div className="card-clinical p-4">
          <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <FirstAidKit size={18} className="text-teal-600" />
            CMAs ({cmas.length})
          </h3>
          <div className="space-y-2">
            {cmas.slice(0, 5).map(cma => (
              <div key={cma.id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
                    <FirstAidKit size={14} className="text-teal-600" />
                  </div>
                  <span className="text-sm font-medium">{cma.first_name} {cma.last_name}</span>
                </div>
                <span className="text-xs text-slate-500">{cma.email}</span>
              </div>
            ))}
            {cmas.length === 0 && (
              <p className="text-sm text-slate-500 text-center py-2">No CMAs registered</p>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card-clinical p-4">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search by patient or provider name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-teal-500"
          >
            <option value="all">All Status</option>
            <option value="scheduled">Scheduled</option>
            <option value="checked_in">Checked In</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Today's Telehealth Appointments */}
      <div className="card-clinical overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-blue-50">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <Calendar size={18} className="text-blue-600" />
            Today's Telehealth Visits ({todayAppointments.length})
          </h3>
        </div>

        {todayAppointments.length === 0 ? (
          <div className="p-8 text-center">
            <VideoCamera size={48} className="text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No telehealth visits scheduled for today</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {todayAppointments.map((apt) => (
              <div key={apt.id} className="p-4 hover:bg-slate-50" data-testid={`telehealth-apt-${apt.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="text-center min-w-[70px]">
                      <p className="text-lg font-semibold text-slate-900">{formatTime(apt.scheduled_time)}</p>
                      <p className="text-xs text-slate-500">{apt.duration_minutes || 30} min</p>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <User size={16} className="text-slate-400" />
                        <p className="font-medium text-slate-900">{apt.patient_name}</p>
                        {getStatusBadge(apt.status)}
                      </div>
                      {apt.provider_name && (
                        <p className="text-sm text-slate-500">
                          <Stethoscope size={14} className="inline mr-1" />
                          Provider: {apt.provider_name}
                        </p>
                      )}
                      {/* Contact Info */}
                      <div className="flex items-center gap-3 mt-2">
                        {apt.patient_phone && (
                          <a 
                            href={`tel:${apt.patient_phone}`}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-teal-50 hover:bg-teal-100 text-teal-700 rounded text-xs"
                          >
                            <Phone size={12} /> {apt.patient_phone}
                          </a>
                        )}
                        {apt.patient_email && (
                          <a 
                            href={`mailto:${apt.patient_email}`}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded text-xs"
                          >
                            <Envelope size={12} /> {apt.patient_email}
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                  {apt.meeting_room_url && (
                    <a
                      href={apt.meeting_room_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm flex items-center gap-2"
                    >
                      <VideoCamera size={16} /> Join Call
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upcoming Telehealth Appointments */}
      {upcomingAppointments.length > 0 && (
        <div className="card-clinical overflow-hidden">
          <div className="p-4 border-b border-slate-200 bg-teal-50">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <HourglassHigh size={18} className="text-teal-600" />
              Upcoming Telehealth Visits ({upcomingAppointments.length})
            </h3>
          </div>
          <div className="divide-y divide-slate-100">
            {upcomingAppointments.slice(0, 10).map((apt) => (
              <div key={apt.id} className="p-4 hover:bg-slate-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="text-center min-w-[100px]">
                      <p className="text-sm font-medium text-slate-900">{formatDate(apt.scheduled_time)}</p>
                      <p className="text-lg font-semibold text-teal-600">{formatTime(apt.scheduled_time)}</p>
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{apt.patient_name}</p>
                      {apt.provider_name && (
                        <p className="text-sm text-slate-500">Provider: {apt.provider_name}</p>
                      )}
                    </div>
                  </div>
                  {getStatusBadge(apt.status)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
